import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { cloneDeep } from 'lodash';
import moment from 'moment/moment';
import {
	SdfBadge,
	SdfButton,
	SdfCard,
	SdfIcon,
	SdfInput,
	SdfLink,
	SdfMultiSelect,
	SdfQuickStat,
	SdfTag
} from '@synerg/react-components';
import usFlag from '../assets/us-flag-5x.png';
import caFlag from '../assets/ca-flag-5x.png';
import { withSession } from '../../session';
//import ProjectIllustration from './projectIllustration';
import './productionProjects.scss';

function ProductionProjects({ session = null, products, projects, jumpScrollIdProd,
	setJumpScrollIdDev, setJumpScrollIdProd, partnerName, fullName, isDataCloudUser }) {

	const [searchTerm, setSearchTerm] = useState('');
	const [showProjects, setShowProjects] = useState(projects.slice(0, 3) || []);
	const [selectedProducts, setSelectedProducts] = useState([]);
	const [hideFilters, setHideFilters] = useState(true);
	const history = useHistory();

	useEffect(() => {
		if (jumpScrollIdProd) {
			if (hideFilters) {
				setHideFilters(false);
			} else {
				doJumpScroll();
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [jumpScrollIdProd]);

	useEffect(() => {
		if (!hideFilters) {
			searchProjects();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [searchTerm, selectedProducts]);

	useEffect(() => {
		if (!hideFilters) {
			setShowProjects(projects);
		} else {
			clearAllFilters();
			setShowProjects(projects.slice(0, 3) || []);
		}
		doJumpScroll();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [hideFilters]);

	const doJumpScroll = () => {
		if (jumpScrollIdProd) {
			clearAllFilters();
			setTimeout(() => {
				const elem = document.getElementById(jumpScrollIdProd);
				if (elem) {
					elem.scrollIntoView({
						behavior: 'smooth',
						block: 'center',
						inline: 'center'
					});
					elem.classList.add('focus-glow');
				}
			}, 500);
			setJumpScrollIdProd(null);
		}
	};

	function clearAllFilters() {
		setSelectedProducts([]);
		setSearchTerm('');
	}

	function removeTag(tag) {
		setSelectedProducts(selectedProducts.filter(product => product !== tag));
		searchProjects();
	}

	function searchProjects() {
	   let s = searchTerm.toLowerCase();
	   let results = cloneDeep(projects) || [];
	   // Find all projects filtered by the search term first
	   if (searchTerm && searchTerm.length > 0) {
		   results = projects.filter((project) => {
			   let name = project.name;
			   return name.toLowerCase().includes(s);
		   });
	   }
	   // Next we apply the products filters on those filtered projects
		if (selectedProducts && selectedProducts.length > 0) {
			let bucket = [];
			for (let item of selectedProducts) {
				// eslint-disable-next-line array-callback-return
				const filteredResult = results.filter(
				(project) =>
					project.product &&
					project.product.toLowerCase() === item.label.toLowerCase()
				);
				bucket = bucket.concat(filteredResult);
			}
			results = bucket;
		}
		// Lastly we set the filtered by products & search term list to show
	    setShowProjects(results);
	}

	const routeChange = (path) => {
		history.push(path);
	};

	const getProdProjectIdAndRedirect = (pid='') => {
		routeChange(`/projects/${pid}`);
	};

	return (
		<div className="bg-action-default-100 mt-8 rounded-xl p-2 h-full">
				<div className="pt-4 px-10">
					<div className="flex my-4 mx-0 items-center justify-between">
						<div className="col-span-1">
							<div className="text-heading-06">
								<span className="w-lg leading-xs tracking-xs font-black">My Production Projects </span>
								<span>
                  {"(" + showProjects.length + " of " + projects.length + ")"}
								</span>
							</div>
						</div>
						{hideFilters && <SdfLink className='mt-1 text-md text-left cursor-pointer' emphasis="none" slot="cta" onClick={() => {setHideFilters(false)}}>
							Show all production projects<SdfIcon icon="action-expand" />
						</SdfLink>}

						{!hideFilters && <SdfLink className='mt-1 text-md text-left cursor-pointer' emphasis="none" slot="cta" onClick={() => {setHideFilters(true)}}>
							Hide production projects<SdfIcon icon="action-collapse" />
						</SdfLink>}
					</div>

					{!hideFilters && !isDataCloudUser && <div className="bg-action-default-50 mt-8 mb-2 rounded-xl">
							<div className="pt-8 px-6">
								<div className="flex items-start" style={{height: "4rem"}}>
									<SdfInput
										className="w-3/4 pr-6"
										label="Search"
										value={searchTerm}
										placeholder="Search by production project name"
										onSdfInput={($event) => {
											setSearchTerm($event.detail);
										}}
									>
										<SdfIcon slot="after" icon="action-search" />
									</SdfInput>
									<SdfMultiSelect
										className="w-1/4"
										placeholder={selectedProducts.length > 0 ? 'Selected' : 'Select Product'}
										label="Filter by ADP Product"
										value={selectedProducts}
										items={products}
										input_width="xl"
										onSdfChange={({detail}) => {
											setSelectedProducts(detail);
										}}
									/>
								</div>
							</div>
							<div className="flex flex-wrap tags-section pl-6 pb-8">
								{selectedProducts.map((product, index) => (<SdfTag key={index} className="bg-white rounded-full mr-2 mt-4" onClick={() => removeTag(product)} value={product.label} />))}
								{selectedProducts.length > 0 &&
									<SdfButton onClick={() => {setSelectedProducts([])
										setSearchTerm('')}} className="ml-2 mt-4" variant="text">
										Clear all
									</SdfButton>
								}
							</div>
					</div>}
				</div>

			{showProjects && <div className={`p-2 items-center content-center mx-8 overflow-auto max-h-2xl`}>
				{showProjects.length > 0 && showProjects.map(({name, platform, status, link, legacy, b2cConsumerAppID, b2bConsumerAppID, createdDate, _id,
															  caseDisplay, product, appStore}, index) => {
					function getStatus(status) {
						return status === 'NOT_LIVE' ? 'Not Live' : 'Live';
					}

					function getProjectType(b2bConsumerAppID, b2cConsumerAppID) {
						return [
							b2cConsumerAppID ? 'End-user/SSO' : '',
							b2bConsumerAppID ? 'Data connector' : ''
						].join((b2bConsumerAppID && b2cConsumerAppID) ? ' + ' : '');
					}

					return (
						<SdfCard key={_id} id={_id} className="flex flex-col h-[18rem] mb-4 overflow-hidden">
							<div className="justify-between">
								<div className="flex flex-row-reverse">
									{link && <div className="grid col-span-1 justify-end">
										<SdfLink className="cursor-pointer" external="">
											<SdfIcon icon="action-link"  onClick={() => {
												// firs remove any old highlighted sections
												const highlightedElem = document.getElementsByClassName("focus-glow");
												if (highlightedElem) {
													while (highlightedElem[0]) {
														highlightedElem[0].classList.remove('focus-glow');
													}
												}
												setJumpScrollIdDev(link);
											}} />
										</SdfLink>
									</div>}
								</div>
								<div className="flex flex-row items-center" slot={'title'}>
									<span className="text-heading-06 font-bold capitalize">{name}</span>
									<SdfBadge className="ml-2 capitalize"
										status={status === 'LIVE' ? 'complete' : 'changed'}
									>
										{getStatus(status)}
									</SdfBadge>
									{legacy === true && <SdfBadge className="ml-1" status="default">
										Legacy
									</SdfBadge>}
								</div>
								<div className={isDataCloudUser ? 'my-2 ml-1': ''}>
									<div className='flex justify-between items-center flex-wrap w-full items-start'>
										<SdfQuickStat className='flex order-1 justify-start min-w-1/4' labelPosition='after' label={`Added ${moment(createdDate).format('MMM DD, YYYY')}`}>
											<div className="text-heading-03">{getProjectType(b2bConsumerAppID, b2cConsumerAppID)}</div>
										</SdfQuickStat>
										<SdfQuickStat size="sm" className='flex order-2 min-w-1/6 max-w-1/6 truncate text-ellipsis' labelPosition='before' label='ADP product'>
											<sdf-tooltip className='order-2 text-heading-03'>
												<div slot="tooltip-target" className='truncate text-ellipsis capitalize'>{product || 'NA'}</div>
												<span className='capitalize'>{product || 'NA'}</span>
											</sdf-tooltip>
										</SdfQuickStat>
										<SdfQuickStat size="sm" style={isDataCloudUser ? {maxWidth: '200px'} : {}} className='flex order-3 text-heading-03 min-w-1/6 max-w-1/5 text-ellipsis' labelPosition='before' label='Use case'>
											<sdf-tooltip className='order-2 text-heading-03'>
												<div slot="tooltip-target" className='truncate text-ellipsis capitalize'>{caseDisplay || 'NA'}</div>
												<span className='capitalize'>{caseDisplay || 'NA'}</span>
											</sdf-tooltip>
										</SdfQuickStat>
										{ !isDataCloudUser && <SdfQuickStat className='flex order-4 min-w-1/8 max-w-1/5' labelPosition='before' label='Locale'>
											<img className="order-2 w-9" alt="" src={appStore === 'US' ? usFlag : caFlag} />
										</SdfQuickStat> }
										<div className='flex order-5 justify-end max-w-1/8'>
											<SdfButton emphasis={"secondary"} onClick={() => getProdProjectIdAndRedirect(_id)}>
												View project
											</SdfButton>
										</div>
									</div>
								</div>
							</div>
						</SdfCard>
					)
				})}

				{/*Show Create New Projects*/}
				{/* {showProjects.length === 0 && <ProjectIllustration fullName={fullName} partnerName={partnerName} illusTitle={"You have no production projects yet!"} />} */}
			</div>}
		</div>
	);
}

export default withSession(ProductionProjects);
